/**
 * fetchInterceptor.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import { getPostLogoutPath } from './postLogoutLocation';

// Global fetch interceptor to handle 401 errors automatically
let logoutCallback = null;
let isInterceptorInstalled = false;

export const setLogoutCallback = (callback) => {
  logoutCallback = callback;
};

export const installFetchInterceptor = () => {
  if (isInterceptorInstalled) {
    console.log('Fetch interceptor already installed');
    return;
  }

  // Store the original fetch
  const originalFetch = window.fetch;

  // Override the global fetch
  window.fetch = async function(...args) {
    try {
      const response = await originalFetch(...args);
      
      // Check for 401 Unauthorized (token expired or invalid)
      if (response.status === 401) {
        const url = args[0];
        
        // Skip auth handling for public endpoints that shouldn't trigger logout
        const publicEndpoints = [
          '/api/contact/send', 
          '/api/contact/', 
          '/api/public/',
          '/api/content/pages/',
          '/api/content/lookup/',
          '/api/products/',
          '/api/content/products/',
          '/api/content/tunes/'
        ];
        const isPublicEndpoint = typeof url === 'string' && 
          publicEndpoints.some(endpoint => url.includes(endpoint));
        
        if (isPublicEndpoint) {
          // Don't trigger logout for public endpoints - just log and return response
          console.info('401 error on public endpoint - not triggering logout:', url);
          return response;
        }
        
        console.warn('Received 401 Unauthorized - token expired or invalid');
        
        // For protected API endpoints, always trigger logout on 401
        // This handles token expiration, invalid tokens, and missing tokens
        if (typeof url === 'string' && url.includes('/api/')) {
          console.log('401 error on protected API route, triggering auto-logout');
          
          // Try to parse the error response for logging
          try {
            const errorData = await response.clone().json();
            console.log('401 Error details:', errorData.error || 'Token expired or invalid');
          } catch (e) {
            // Error response is not JSON, but still trigger logout
            console.log('401 error (non-JSON response), triggering logout');
          }
          
          // Call the logout callback if available (logout function now handles redirect)
          if (logoutCallback) {
            logoutCallback();
          } else {
            // Fallback: redirect to login if callback not set
            console.warn('Logout callback not set, redirecting manually');
            localStorage.removeItem('token');
            sessionStorage.removeItem('currentUser');
            const nextPath = getPostLogoutPath(window.location.pathname);
            if (nextPath) {
              window.location.assign(nextPath);
            }
          }
        }
      }
      
      return response;
    } catch (error) {
      console.error('Fetch error:', error);
      throw error;
    }
  };

  isInterceptorInstalled = true;
  console.log('Fetch interceptor installed successfully');
};

// Uninstall the interceptor (for cleanup if needed)
export const uninstallFetchInterceptor = () => {
  // Note: This won't restore the original fetch since we don't have a reference to it anymore
  // This is mainly for completeness
  isInterceptorInstalled = false;
};

